import { MAX_VERSIONS, type VersionDetail, type VersionSummary } from '../../shared/types'
import { ApiError, fetchVersion, listVersions, restoreVersion } from '../api'
import { formatBytes, formatRelative, h, plural, toast } from '../ui'

/**
 * The edit history for one pasta, as a strip under the toolbar. Picking a
 * revision shows what changed between it and the text that's current now;
 * restoring it is a normal edit, so the current text becomes a revision too
 * and nothing is lost by trying.
 */

export interface HistoryOptions {
  pasteId: string
  /** Shows the diff for a revision, or returns to the current text with null. */
  onSelect: (version: VersionDetail | null) => void
  /** Called after a successful restore, once the pasta has actually changed. */
  onRestored: () => void
}

export interface HistoryPanel {
  element: HTMLElement
  destroy: () => void
}

export function historyPanel(options: HistoryOptions): HistoryPanel {
  const rows = h('div', { class: 'history-rows' }, [h('span', { class: 'muted' }, ['Loading…'])])
  const element = h('div', { class: 'history' }, [rows])

  let disposed = false
  let versions: VersionSummary[] = []
  let selected: number | null = null
  let busy = false

  void load()

  async function load(): Promise<void> {
    try {
      const result = await listVersions(options.pasteId)
      if (disposed) return
      versions = result.versions
      render()
    } catch (error) {
      if (disposed) return
      rows.replaceChildren(
        h('span', { class: 'muted' }, [
          error instanceof ApiError ? error.message : 'Could not load the history.',
        ]),
      )
    }
  }

  function render(): void {
    if (!versions.length) {
      rows.replaceChildren(
        h('span', { class: 'muted' }, ['No edits yet — this is the only version there has been.']),
      )
      return
    }

    const cap =
      versions.length >= MAX_VERSIONS
        ? [h('span', { class: 'muted history-cap' }, [`keeps the last ${MAX_VERSIONS}`])]
        : []

    rows.replaceChildren(
      h('span', { class: 'history-label' }, [plural(versions.length, 'earlier version')]),
      currentChip(),
      ...versions.map((version) => chip(version)),
      ...cap,
    )
  }

  function currentChip(): HTMLElement {
    return h(
      'button',
      {
        class: `history-chip${selected === null ? ' selected' : ''}`,
        type: 'button',
        onclick: () => {
          selected = null
          render()
          options.onSelect(null)
        },
      },
      ['current'],
    )
  }

  function chip(version: VersionSummary): HTMLElement {
    return h('span', { class: 'history-entry' }, [
      h(
        'button',
        {
          class: `history-chip${selected === version.id ? ' selected' : ''}`,
          type: 'button',
          title: `Replaced ${new Date(version.replacedAt).toLocaleString()} · ${formatBytes(version.sizeBytes)}`,
          onclick: () => void show(version),
        },
        [formatRelative(version.replacedAt)],
      ),
      selected === version.id
        ? h(
            'button',
            {
              class: 'control history-restore',
              type: 'button',
              onclick: () => void restore(version),
            },
            ['Restore this'],
          )
        : null,
    ])
  }

  async function show(version: VersionSummary): Promise<void> {
    try {
      const detail = await fetchVersion(options.pasteId, version.id)
      if (disposed) return
      selected = version.id
      render()
      options.onSelect(detail)
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Could not load that version')
    }
  }

  async function restore(version: VersionSummary): Promise<void> {
    if (busy) return
    const when = formatRelative(version.replacedAt)
    if (!confirm(`Put the version from ${when} back? The current text is kept in the history.`)) {
      return
    }
    busy = true
    try {
      await restoreVersion(options.pasteId, version.id)
      if (disposed) return
      toast('Restored')
      options.onRestored()
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Could not restore that version')
    } finally {
      busy = false
    }
  }

  return {
    element,
    destroy: () => {
      disposed = true
      element.remove()
    },
  }
}
